import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/search/PageHeader';
import DepartureInfo from '../components/search/DepartureInfo';
import DateSelector from '../components/search/DateSelector';
import FilterBar from '../components/search/FilterBar';
import FlightResults from '../components/search/FlightResults';
import FlightDetailsCard from '../components/search/FlightDetailsCard';
import FlightDetailsDialog from '../components/flight/FlightDetailsDialog';
import CalendarDialog from '../components/search/CalendarDialog';
import { searchFlights, formatCityDisplay, updateFlightPricesForDate } from '../utils/flightSearch';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState('Main');
  const [stopsFilter, setStopsFilter] = useState('Any number of stops');
  const [selectedFlightDetails, setSelectedFlightDetails] = useState(null);
  const [flightDetailsDialog, setFlightDetailsDialog] = useState({ isOpen: false, flightData: null });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // Round-trip flight selection state
  const [selectedDepartFlight, setSelectedDepartFlight] = useState(null);
  const [currentFlightLeg, setCurrentFlightLeg] = useState('depart'); // 'depart' or 'return'
  
  // Ref for FlightDetailsCard to enable smooth scrolling
  const flightDetailsCardRef = useRef(null);

  // Smooth scroll to FlightDetailsCard when it appears
  useEffect(() => {
    if (selectedFlightDetails && flightDetailsCardRef.current) {
      // Small delay to ensure the card is rendered
      setTimeout(() => {
        flightDetailsCardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [selectedFlightDetails]);

  // Get search parameters from URL
  const fromCode = searchParams.get('from') || 'JFK';
  const toCode = searchParams.get('to') || 'LAX';
  const tripType = searchParams.get('tripType') || 'roundTrip';
  const departDate = searchParams.get('departDate');
  const returnDate = searchParams.get('returnDate');
  const adults = parseInt(searchParams.get('adults')) || 1;
  const redeemMilesParam = searchParams.get('redeemMiles') === 'true';
  
  const [selectedDate, setSelectedDate] = useState(
    departDate ? new Date(departDate).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    }) : new Date().toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })
  );
  
  // Get filtered flights based on search criteria
  const baseSearchResults = useMemo(() => {
    return searchFlights({
      fromCode,
      toCode,
      departDate,
      tripType
    });
  }, [fromCode, toCode, departDate, tripType]);
  
  // Update flights when date selector changes
  const searchResults = useMemo(() => {
    // Convert selectedDate format to ISO date
    let dateToUse = departDate;
    
    if (selectedDate) {
      // selectedDate comes as "Thu, Aug 26" format, need to convert to proper date
      const currentYear = new Date().getFullYear();
      const dateParts = selectedDate.split(', ')[1]; // "Aug 26"
      const [month, day] = dateParts.split(' ');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.indexOf(month);
      
      if (monthIndex !== -1) {
        const newDate = new Date(currentYear, monthIndex, parseInt(day));
        dateToUse = newDate.toISOString();
      }
    }
    
    return updateFlightPricesForDate(baseSearchResults, dateToUse);
  }, [baseSearchResults, selectedDate, departDate]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleClassChange = (className) => {
    setSelectedClass(className);
  };

  const handleStopsFilterChange = (filterValue) => {
    setStopsFilter(filterValue);
  };

  const handleClearFilters = () => {
    setSelectedClass('Main');
    setStopsFilter('Any number of stops');
    // We would also reset airlines and airports filters here if they were in this component
  };

  const handleFareSelection = (fareData, flightData) => {
    console.log('Fare selected in SearchResults:', fareData, flightData);
    
    if (tripType === 'roundTrip') {
      if (currentFlightLeg === 'depart') {
        // Select depart flight and switch to return leg
        setSelectedDepartFlight({ fareData, flightData });
        setCurrentFlightLeg('return');
        setSelectedFlightDetails({ fareData, flightData });
      } else {
        // Select return flight - complete the round trip booking
        const completedSelection = { 
          departFlight: selectedDepartFlight,
          returnFlight: { fareData, flightData }
        };
        setSelectedFlightDetails(completedSelection);
        console.log('Round trip completed:', completedSelection);
        
        // Navigate to checkout with selected flight data
        navigate('/checkout', { 
          state: { 
            selectedFlights: completedSelection,
            tripType: tripType,
            searchParams: {
              from: fromCode,
              to: toCode,
              departDate: departDate,
              returnDate: returnDate,
              adults: adults
            }
          } 
        });
      }
    } else {
      // One-way trip
      const oneWaySelection = { fareData, flightData };
      setSelectedFlightDetails(oneWaySelection);
      
      // Navigate to checkout with selected flight data
      navigate('/checkout', { 
        state: { 
          selectedFlights: oneWaySelection,
          tripType: tripType,
          searchParams: {
            from: fromCode,
            to: toCode,
            departDate: departDate,
            adults: adults
          }
        } 
      });
    }
  };

  const handleShowFlightDetails = (flightData) => {
    console.log('Opening flight details for:', flightData);
    setFlightDetailsDialog({ isOpen: true, flightData: flightData });
  };

  const handleCloseFlightDetails = () => {
    console.log('Closing flight details dialog');
    setFlightDetailsDialog({ isOpen: false, flightData: null });
  };

  const handleOpenCalendar = () => {
    setIsCalendarOpen(true);
  };

  const handleCloseCalendar = () => {
    setIsCalendarOpen(false);
  };

  const handleCalendarDateSelect = (dateData) => {
    console.log('Calendar date selected:', dateData);
    if (typeof dateData === 'object' && dateData.departDate) {
      // Round trip dates
      setSelectedDate(dateData.departDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }));
    } else if (dateData instanceof Date) {
      // One way date
      setSelectedDate(dateData.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }));
    }
  };

  const handleMilesFlightSelection = (flight, fareType) => {
    console.log("Miles flight selected:", flight, fareType);
    
    const fareData = {
      type: fareType === 'main' ? 'Main' : 'First',
      id: fareType === 'main' ? 'main' : 'first',
      price: fareType === 'main' ? flight.mainMiles : flight.firstMiles
    };
    
    // Follow the same logic as regular fare selection
    if (tripType === 'roundTrip') {
      if (currentFlightLeg === 'depart') {
        // Select depart flight and switch to return leg
        setSelectedDepartFlight({ fareData, flightData: flight });
        setCurrentFlightLeg('return');
        setSelectedFlightDetails({ fareData, flightData: flight });
      } else {
        // Select return flight - complete the round trip booking
        const completedSelection = { 
          departFlight: selectedDepartFlight,
          returnFlight: { fareData, flightData: flight }
        };
        setSelectedFlightDetails(completedSelection);
        console.log('Round trip miles completed:', completedSelection);
        
        // Navigate to checkout with selected flight data
        navigate('/checkout', { 
          state: { 
            selectedFlights: completedSelection,
            tripType: tripType,
            searchParams: {
              from: fromCode,
              to: toCode,
              departDate: departDate,
              returnDate: returnDate,
              adults: adults
            }
          } 
        });
      }
    } else {
      // One-way trip
      const oneWaySelection = { fareData, flightData: flight };
      setSelectedFlightDetails(oneWaySelection);
      
      // Navigate to checkout with selected flight data
      navigate('/checkout', { 
        state: { 
          selectedFlights: oneWaySelection,
          tripType: tripType,
          searchParams: {
            from: fromCode,
            to: toCode,
            departDate: departDate,
            adults: adults
          }
        } 
      });
    }
  };

  // Helper function to check if flight matches selected class
  const matchesClassFilter = (flight, classFilter) => {
    if (classFilter === 'Main') {
      return true; // Main shows all classes
    }
    
    switch (classFilter) {
      case 'Basic Economy':
        // Basic Economy is only available on international flights
        return flight.routeType === 'international' && flight.mainAvailable;
      case 'Premium Economy':
      case 'Business':
        return flight.premiumAvailable;
      default:
        return true;
    }
  };

  // Calculate filtered results count
  const filteredResultsCount = useMemo(() => {
    return searchResults.filter(flight => {
      // Check stops filter
      const matchesStops = (() => {
        switch (stopsFilter) {
          case 'Nonstop only':
            return flight.stops === 'Nonstop';
          case '1 stop or fewer':
            return flight.stops === 'Nonstop' || flight.stops === '1 stop';
          case '2 stops or fewer':
            return flight.stops === 'Nonstop' || flight.stops === '1 stop' || flight.stops === '2 stops';
          case 'Any number of stops':
          default:
            return true;
        }
      })();
      
      // Check class filter
      const matchesClass = matchesClassFilter(flight, selectedClass);
      
      return matchesStops && matchesClass;
    }).length;
  }, [searchResults, stopsFilter, selectedClass]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1240px] mx-auto px-4 py-6">
        <PageHeader />
        
        {/* Selected Flight Details Card - appears below "New search" */}
        {selectedFlightDetails && (
          <div ref={flightDetailsCardRef}>
            <FlightDetailsCard
              fareData={selectedFlightDetails.fareData}
              flightData={selectedFlightDetails.flightData}
              onClose={() => setSelectedFlightDetails(null)}
              onShowDetails={handleShowFlightDetails}
            />
          </div>
        )}
        
        <DepartureInfo 
          from={currentFlightLeg === 'return' ? formatCityDisplay(toCode) : formatCityDisplay(fromCode)} 
          to={currentFlightLeg === 'return' ? formatCityDisplay(fromCode) : formatCityDisplay(toCode)} 
          date={currentFlightLeg === 'return' && returnDate ? 
            new Date(returnDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }) : 
            (departDate ? new Date(departDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }) : new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }))
          }
          type={tripType === 'roundTrip' && currentFlightLeg === 'return' ? 'RETURN' : 'DEPART'}
        />
        <div className="grid grid-cols-12  mb-4" style={{border: '1px solid rgb(228, 228, 228)', borderRadius: '5px', boxShadow: '0 1px 8px rgba(0, 0, 0, 0.2)'}}>
          <div className={`${redeemMilesParam ? 'col-span-9' : 'col-span-12'}`} style={{borderRight: '1px solid rgb(228, 228, 228)'}}>
            <DateSelector 
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              fromCode={fromCode}
              toCode={toCode}
              searchResults={baseSearchResults}
            />
          </div>
          {redeemMilesParam && (
            <div className="col-span-3 flex items-center justify-center hover:bg-[#0078d2] hover:text-white ">
              <button 
                className='flex items-center justify-center cursor-pointer text-aa-blue  p-4 transition-colors hover:bg-[#0078d2] hover:text-white'
                onClick={handleOpenCalendar}
                style={{height: '75px', width: '100%'}}
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.89-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
                <span className="text-sm font-bold tracking-wide mr-2">CALENDAR</span>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 12 12">
                  <path d="M6 8L2 4h8L6 8z" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <FilterBar 
          selectedClass={selectedClass}
          onClassChange={handleClassChange}
          resultsCount={filteredResultsCount}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          onClearFilters={handleClearFilters}
          fromCode={fromCode}
          toCode={toCode}
          searchResults={searchResults}
          isRedeemMiles={redeemMilesParam}
        />

        <FlightResults 
          selectedClass={selectedClass}
          stopsFilter={stopsFilter} 
          onClearFilters={handleClearFilters}
          searchResults={searchResults}
          onFareSelection={handleFareSelection}
          fromCode={fromCode}
          toCode={toCode}
          tripType={tripType}
          isRedeemMiles={redeemMilesParam}
          onFlightSelect={handleMilesFlightSelection}
        />
      </div>
      
      {/* Flight Details Dialog */}
      <FlightDetailsDialog
        isOpen={flightDetailsDialog.isOpen}
        onClose={handleCloseFlightDetails}
        flightData={flightDetailsDialog.flightData}
      />

      {/* Calendar Dialog - Only show when redeem miles is enabled */}
      {redeemMilesParam && (
        <CalendarDialog
          isOpen={isCalendarOpen}
          onClose={handleCloseCalendar}
          onDateSelect={handleCalendarDateSelect}
          selectedDate={selectedDate}
          returnDate={returnDate}
          tripType={tripType}
        />
      )}
    </div>
  );
};

export default SearchResults;